import { useState, useCallback, useMemo, useEffect } from 'react';
import { Measure, TimeSignature, ActiveChord, AudioSettings, KeySignature, SavedChartState, UISettings, MeasureAnnotation, RecordedMidiNote } from '../types';
import { transposeChord, parseChordToNotes, formatChordNotatorChord } from '../utils/audio';
import { FeatureKey } from '../utils/features';
import { CURRENT_CHART_SCHEMA_VERSION, migrateChartToCurrent } from '../utils/chartMigrations';

// ── Helper functions (moved from App.tsx) ──────────────────────────

export const generateInitialChart = (count: number): Measure[] => {
  const uniqueIdBase = Date.now();
  const measures: Measure[] = Array.from({ length: count }, (_, i) => ({
    id: uniqueIdBase + i,
    chords: Array(1).fill(''),
    lyrics: '',
    annotations: undefined,
  }));
  return measures;
};

export const getDefaultState = (): SavedChartState => ({
  chartSchemaVersion: CURRENT_CHART_SCHEMA_VERSION,
  songTitle: '',
  songAuthor: '',
  songStyle: 'Swing',
  numberOfMeasures: 16,
  timeSignature: { beats: 4, beatType: 4 },
  keySignature: 'C',
  sourceChart: generateInitialChart(16),
  transposeAmount: 0,
  audioSettings: {
    timbre: 'Piano',
    voicing: 'Spread',
    dynamics: 'Block',
    bpm: 87,
    metronomeEnabled: true,
    synth: {
      waveform: 'sawtooth',
      attack: 0.02,
      decay: 0.1,
      sustain: 0.6,
      release: 0.3,
      filterType: 'lowpass',
      filterCutoff: 2500,
      filterResonance: 5,
    },
  },
  uiSettings: {
    inspectorEnabled: false,
    inspectorMode: 'piano',
    lyricsFontSize: 14,
    isDebugOverlayVisible: false,
    debugOverlayLevel: 1,
  },
  recordedNotes: [],
  lastModified: new Date().toISOString(),
});

// ── Types ──────────────────────────────────────────────────────────

export interface ChartStateActions {
  setSongTitle: React.Dispatch<React.SetStateAction<string>>;
  setSongAuthor: React.Dispatch<React.SetStateAction<string>>;
  setSongStyle: React.Dispatch<React.SetStateAction<string>>;
  setNumberOfMeasures: React.Dispatch<React.SetStateAction<number>>;
  setTimeSignature: React.Dispatch<React.SetStateAction<TimeSignature>>;
  setKeySignature: React.Dispatch<React.SetStateAction<KeySignature>>;
  setSourceChart: React.Dispatch<React.SetStateAction<Measure[]>>;
  setTransposeAmount: React.Dispatch<React.SetStateAction<number>>;
  setAudioSettings: React.Dispatch<React.SetStateAction<AudioSettings>>;
  setUiSettings: React.Dispatch<React.SetStateAction<UISettings>>;
  setActiveChord: React.Dispatch<React.SetStateAction<ActiveChord | null>>;
  setRecordedNotes: React.Dispatch<React.SetStateAction<RecordedMidiNote[]>>;
  updateSourceChart: (updater: (chart: Measure[]) => Measure[]) => void;
  applyLoadedState: (state: SavedChartState) => void;
  handleUndo: () => void;
  handleNumMeasuresChange: (newSize: number, mode: string, startMeasureIndex: number, setStartMeasureIndex: (v: number) => void) => void;
  handleSubdivisionChange: (measureIndex: number, newSubdivision: number) => void;
  handleChordClick: (measureIndex: number, chordIndex: number) => void;
  handleChordBuild: (type: 'root' | 'accidental' | 'quality' | 'alteration' | 'action', value: string) => void;
  handleAnnotateMeasure: (measureIndex: number, type: keyof MeasureAnnotation, value: any) => void;
  handleLyricChange: (measureIndex: number, newLyric: string) => void;
  handleCopyMeasure: (measureIndex: number) => void;
  handlePasteMeasure: (measureIndex: number) => void;
  handleTimeSignatureChange: (newBeats: number) => void;
  handleTranspose: (amount: number) => void;
  handleLyricsSizeChange: (amount: number) => void;
  handleGigChordScaleChange: (delta: number) => void;
}

export interface UseChartStateReturn extends ChartStateActions {
  songTitle: string;
  songAuthor: string;
  songStyle: string;
  numberOfMeasures: number;
  timeSignature: TimeSignature;
  keySignature: KeySignature;
  sourceChart: Measure[];
  transposeAmount: number;
  audioSettings: AudioSettings;
  uiSettings: UISettings;
  history: Measure[][];
  activeChord: ActiveChord | null;
  invalidChords: Record<string, string | null>;
  clipboard: (string | null)[] | null;
  displayedChart: Measure[];
  repeatPairMap: { [endIndex: number]: number };
  recordedNotes: RecordedMidiNote[];
}

// ── Hook ───────────────────────────────────────────────────────────

export function useChartState(
  mode: string,
  isReadOnlyMode: boolean,
  hasFeature: (feature: FeatureKey) => boolean,
  upgradeToPremium: (feature: FeatureKey) => void,
): UseChartStateReturn {
  // ── State ──────────────────────────────────────────────────────
  const [songTitle, setSongTitle] = useState<string>('');
  const [songAuthor, setSongAuthor] = useState<string>('');
  const [songStyle, setSongStyle] = useState<string>('Swing');
  const [numberOfMeasures, setNumberOfMeasures] = useState<number>(16);
  const [timeSignature, setTimeSignature] = useState<TimeSignature>({ beats: 4, beatType: 4 });
  const [keySignature, setKeySignature] = useState<KeySignature>('C');
  const [sourceChart, setSourceChart] = useState<Measure[]>(generateInitialChart(16));
  const [transposeAmount, setTransposeAmount] = useState<number>(0);
  const [audioSettings, setAudioSettings] = useState<AudioSettings>(getDefaultState().audioSettings);
  const [uiSettings, setUiSettings] = useState<UISettings>(getDefaultState().uiSettings);
  const [recordedNotes, setRecordedNotes] = useState<RecordedMidiNote[]>([]);
  
  const [history, setHistory] = useState<Measure[][]>([]);
  const [activeChord, setActiveChord] = useState<ActiveChord | null>(null);
  const [invalidChords, setInvalidChords] = useState<Record<string, string | null>>({});
  const [clipboard, setClipboard] = useState<(string | null)[] | null>(null);

  // ── Computed ───────────────────────────────────────────────────

  const displayedChart = useMemo(() => {
    if (transposeAmount === 0) return sourceChart;
    return sourceChart.map(measure => ({
      ...measure,
      chords: measure.chords.map(chord => chord ? transposeChord(chord, transposeAmount, keySignature) : chord)
    }));
  }, [sourceChart, transposeAmount, keySignature]);

  const repeatPairMap = useMemo(() => {
    const map: { [endIndex: number]: number } = {};
    const startRepeatStack: number[] = [];

    displayedChart.forEach((measure, measureIndex) => {
      const hasStart = measure.annotations?.startRepeat;
      const hasEnd = measure.annotations?.endRepeat;

      if (hasStart) {
        startRepeatStack.push(measureIndex);
      }
      if (hasEnd) {
        if (startRepeatStack.length > 0) {
          const matchingStartIndex = startRepeatStack.pop();
          if (matchingStartIndex !== undefined) {
            map[measureIndex] = matchingStartIndex;
          }
        }
      }
    });
    return map;
  }, [displayedChart]);

  // ── Effects ────────────────────────────────────────────────────

  // Chord validation effect
  useEffect(() => {
    const newInvalidChords: Record<string, string | null> = {};
    sourceChart.forEach((measure, measureIndex) => {
      measure.chords.forEach((chord, chordIndex) => {
        if (chord) {
          const parsed = parseChordToNotes(chord);
          if (parsed.error) {
            newInvalidChords[`${measureIndex}-${chordIndex}`] = parsed.error;
          }
        }
      });
    });
    setInvalidChords(newInvalidChords);
  }, [sourceChart]);

  // ── Core functions ─────────────────────────────────────────────

  const updateSourceChart = useCallback((updater: (chart: Measure[]) => Measure[]) => {
    setSourceChart(currentChart => {
      setHistory(h => [...h.slice(-20), currentChart]);
      return updater(currentChart);
    });
  }, []);

  const applyLoadedState = useCallback((parsed: SavedChartState) => {
    const migrated = migrateChartToCurrent(parsed);
    const defaults = getDefaultState();
    const loadedAudioSettings = {
      ...defaults.audioSettings,
      ...(migrated.audioSettings || {}),
      synth: {
        ...defaults.audioSettings.synth,
        ...(migrated.audioSettings?.synth || {}),
      }
    };
    
    // Preserve current debug settings when loading a chart
    setUiSettings(currentUiSettings => ({
      ...defaults.uiSettings,
      ...(migrated.uiSettings || {}),
      isDebugOverlayVisible: currentUiSettings.isDebugOverlayVisible,
      debugOverlayLevel: currentUiSettings.debugOverlayLevel,
    }));

    const compatibleSourceChart = migrated.sourceChart.map(m => ({
      ...m,
      chords: m.chords.map(chord => chord ? formatChordNotatorChord(chord) : chord),
      lyrics: m.lyrics || ''
    }));

    setSongTitle(migrated.songTitle);
    setSongAuthor(migrated.songAuthor || '');
    setSongStyle(migrated.songStyle || 'Jazz');
    setNumberOfMeasures(migrated.numberOfMeasures);
    setTimeSignature(migrated.timeSignature);
    setKeySignature(migrated.keySignature);
    setSourceChart(compatibleSourceChart);
    setTransposeAmount(migrated.transposeAmount);
    setAudioSettings(loadedAudioSettings);
    setRecordedNotes(migrated.recordedNotes || []);
    
    setActiveChord(null);
    setHistory([]);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    setSourceChart(lastState);
    setHistory(history.slice(0, -1));
  }, [history]);

  const handleNumMeasuresChange = useCallback((
    newSize: number,
    currentMode: string,
    startMeasureIndex: number,
    setStartMeasureIndex: (v: number) => void
  ) => {
    if (isNaN(newSize) || newSize < 1) return;

    setNumberOfMeasures(newSize);

    setSourceChart(currentChart => {
      const currentLength = currentChart.length;
      if (newSize === currentLength) return currentChart;

      if (newSize > currentLength) {
        const maxId = currentChart.length > 0 ? Math.max(...currentChart.map(m => m.id)) : 0;
        const newMeasures = Array.from({ length: newSize - currentLength }, (_, i) => ({
          id: maxId + 1 + i,
          chords: [''],
          lyrics: '',
        }));
        return [...currentChart, ...newMeasures];
      } else {
        return currentChart.slice(0, newSize);
      }
    });

    if (currentMode === 'edition') {
      setActiveChord(null);
    }
    if (startMeasureIndex >= newSize) {
      setStartMeasureIndex(Math.max(0, newSize - 1));
    }
  }, []);

  const handleSubdivisionChange = useCallback((measureIndex: number, newSubdivision: number) => {
    if (mode !== 'edition') return;
    updateSourceChart(prevChart => {
      const newChart = [...prevChart];
      const measure = { ...newChart[measureIndex] };
      const oldChords = measure.chords;
      const newChords = Array(newSubdivision).fill('');
      for (let i = 0; i < Math.min(oldChords.length, newSubdivision); i++) {
        newChords[i] = oldChords[i];
      }
      measure.chords = newChords;
      newChart[measureIndex] = measure;
      return newChart;
    });
    if (activeChord && activeChord.measureIndex === measureIndex && activeChord.chordIndex >= newSubdivision) {
      setActiveChord(null);
    }
  }, [activeChord, updateSourceChart, mode]);

  const handleChordClick = useCallback((measureIndex: number, chordIndex: number) => {
    if (mode !== 'edition' || isReadOnlyMode) return;
    setActiveChord(prev => 
      prev?.measureIndex === measureIndex && prev?.chordIndex === chordIndex 
      ? null 
      : { measureIndex, chordIndex }
    );
  }, [mode, isReadOnlyMode]);

  const handleChordBuild = useCallback((type: 'root' | 'accidental' | 'quality' | 'alteration' | 'action', value: string) => {
    if (!activeChord) return;
    const { measureIndex, chordIndex } = activeChord;

    updateSourceChart(prevChart => {
      const newChart = JSON.parse(JSON.stringify(prevChart));
      let currentChord: string = newChart[measureIndex].chords[chordIndex] || '';
      let newChord = currentChord;

      const rootRegex = /^[A-G](?:b|#|♭|♯)?/;
      const match = currentChord.match(rootRegex);
      const root = match ? match[0] : '';
      const qualityPart = match ? currentChord.substring(root.length) : currentChord;

      switch (type) {
        case 'root':
          const parts = qualityPart.split('/');
          if (parts.length > 1 && currentChord.endsWith('/')) {
            newChord = currentChord + value;
          } else {
            newChord = value + qualityPart;
          }
          break;
        case 'accidental': {
          const chordParts = currentChord.split('/');
          if (chordParts.length > 1 && chordParts[1].match(/^[A-G]/)) {
            const mainPart = chordParts[0];
            let bassPart = chordParts[1];
            let newBassPart = bassPart;

            if (value === '♯') {
              if (bassPart.endsWith('b') || bassPart.endsWith('♭')) newBassPart = bassPart.slice(0, -1);
              else if (!bassPart.endsWith('#') && !bassPart.endsWith('♯')) newBassPart += '♯';
            } else if (value === '♭') {
              if (bassPart.endsWith('#') || bassPart.endsWith('♯')) newBassPart = bassPart.slice(0, -1);
              else if (!bassPart.endsWith('b') && !bassPart.endsWith('♭')) newBassPart += '♭';
            }
            newChord = `${mainPart}/${newBassPart}`;
          } else {
            if (root) {
              let newRoot = root;
              if (value === '♯') {
                if (root.endsWith('b') || root.endsWith('♭')) newRoot = root.slice(0, -1);
                else if (!root.endsWith('#') && !root.endsWith('♯')) newRoot += '♯';
              } else if (value === '♭') {
                if (root.endsWith('#') || root.endsWith('♯')) newRoot = root.slice(0, -1);
                else if (!root.endsWith('b') && !root.endsWith('♭')) newRoot += '♭';
              }
              newChord = newRoot + qualityPart;
            } else {
              newChord = value;
            }
          }
          break;
        }
        case 'quality': {
          const parenIndex = qualityPart.indexOf('(');
          if (parenIndex > -1) {
            const alterations = qualityPart.substring(parenIndex);
            newChord = root + value + alterations;
          } else {
            newChord = root + value;
          }
          break;
        }
        case 'alteration': {
          const openParenCount = (currentChord.match(/\(/g) || []).length;
          const closeParenCount = (currentChord.match(/\)/g) || []).length;

          if (openParenCount > closeParenCount) {
            if (currentChord.endsWith('(')) {
              newChord = currentChord + value;
            } else {
              newChord = currentChord + ',' + value;
            }
          } else {
            newChord = currentChord + value;
          }
          break;
        }
        case 'action':
          if (value === 'clear') {
            newChord = '';
          } else if (value === '%') {
            newChord = '%';
          } else if (value === '(') {
            const openParenCount = (currentChord.match(/\(/g) || []).length;
            const closeParenCount = (currentChord.match(/\)/g) || []).length;
            if (openParenCount === closeParenCount && !currentChord.endsWith('(')) {
              newChord = currentChord + '(';
            }
          } else if (value === ')') {
            const openParenCount = (currentChord.match(/\(/g) || []).length;
            const closeParenCount = (currentChord.match(/\)/g) || []).length;
            if (openParenCount > closeParenCount && !currentChord.endsWith(')')) {
              newChord = currentChord + ')';
            }
          } else if (value === '/') {
            if (!currentChord.includes('/')) {
              newChord = currentChord + '/';
            }
          } else if (value === 'natural') {
            const chordParts = currentChord.split('/');
            if (chordParts.length > 1 && chordParts[1].match(/^[A-G]/)) {
              const mainPart = chordParts[0];
              let bassPart = chordParts[1];
              const bassRootRegex = /^([A-G])[#b♯♭]?/;
              const bassMatch = bassPart.match(bassRootRegex);
              if (bassMatch) {
                const bassRoot = bassMatch[1];
                const bassQuality = bassPart.substring(bassMatch[0].length);
                newChord = `${mainPart}/${bassRoot}${bassQuality}`;
              }
            } else {
              const rootRegex2 = /^([A-G])[#b♯♭]?/;
              const match2 = currentChord.match(rootRegex2);
              if (match2) {
                const r = match2[1];
                const qp = currentChord.substring(match2[0].length);
                newChord = r + qp;
              }
            }
          }
          break;
      }

      const finalChord = formatChordNotatorChord(newChord.trim());
      newChart[measureIndex].chords[chordIndex] = finalChord;
      return newChart;
    });
  }, [activeChord, updateSourceChart]);

// Helper: clear a contiguous volta block around anchorIndex with the given endingVal
const clearVoltaBlock = (chart: any[], anchorIndex: number, endingVal: number) => {
  let left = anchorIndex;
  while (left > 0 && chart[left - 1]?.annotations?.ending === endingVal) left--;
  let right = anchorIndex;
  while (right < chart.length - 1 && chart[right + 1]?.annotations?.ending === endingVal) right++;
  for (let i = left; i <= right; i++) {
    if (chart[i].annotations) delete chart[i].annotations.ending;
  }
};

  const handleAnnotateMeasure = useCallback((measureIndex: number, type: keyof MeasureAnnotation, value: any) => {
    const isRepeat = [
      'startRepeat',
      'endRepeat',
      'doubleBarline',
      'finalBarline',
      'barlineLeft',
      'barlineRight',
    ].includes(type as string);
    
    if (!isRepeat && !hasFeature('annotationsEditing')) {
      upgradeToPremium('annotationsEditing');
      return;
    }

    updateSourceChart(prevChart => {
      const newChart: typeof prevChart = JSON.parse(JSON.stringify(prevChart));
      const measure = newChart[measureIndex];
      if (!measure.annotations) measure.annotations = {};
      const MAX_SCAN = 16;

      // ── ENDING ─────────────────────────────────────────────────────────────
      if (type === 'ending') {
        const endingVal = value as number;

        if (measure.annotations.ending === endingVal) {
          // Toggle off: clear the whole contiguous block
          clearVoltaBlock(newChart, measureIndex, endingVal);
          return newChart;
        }

        // If this measure was part of another volta block, clear it first
        if (measure.annotations.ending !== undefined) {
          clearVoltaBlock(newChart, measureIndex, measure.annotations.ending);
        }

        // Scan forward for :||
        let endRepeatIdx = -1;
        for (let i = measureIndex; i < Math.min(newChart.length, measureIndex + MAX_SCAN); i++) {
          if (newChart[i].annotations?.endRepeat) { endRepeatIdx = i; break; }
        }

        if (endRepeatIdx !== -1) {
          for (let i = measureIndex; i <= endRepeatIdx; i++) {
            if (!newChart[i].annotations) newChart[i].annotations = {};
            newChart[i].annotations!.ending = endingVal;
          }
        } else {
          measure.annotations.ending = endingVal;
        }
        return newChart;
      }

      // ── END REPEAT ─────────────────────────────────────────────────────────
      if (type === 'endRepeat') {
        if (measure.annotations.endRepeat === true) {
          // Toggle off: remove repeat marker and clean up orphaned volta block
          delete measure.annotations.endRepeat;
          for (let i = measureIndex; i >= Math.max(0, measureIndex - MAX_SCAN); i--) {
            if (newChart[i].annotations?.ending !== undefined) {
              clearVoltaBlock(newChart, i, newChart[i].annotations!.ending!);
              break;
            }
          }
          return newChart;
        }

        // Set endRepeat (right side): remove any conflicting right-side barline
        delete measure.annotations.barlineRight;

        // Set endRepeat: scan backward to extend any nearby volta block
        measure.annotations.endRepeat = true;
        for (let i = measureIndex - 1; i >= Math.max(0, measureIndex - MAX_SCAN); i--) {
          const endingVal = newChart[i].annotations?.ending;
          if (endingVal !== undefined) {
            for (let j = i; j <= measureIndex; j++) {
              if (!newChart[j].annotations) newChart[j].annotations = {};
              newChart[j].annotations!.ending = endingVal;
            }
            break;
          }
        }
        return newChart;
      }

      // ── START REPEAT ───────────────────────────────────────────────────────
      if (type === 'startRepeat') {
        if (measure.annotations.startRepeat === true) {
          delete measure.annotations.startRepeat;
          return newChart;
        }

        // Left side exclusivity: start repeat and left barline can't stack
        delete measure.annotations.barlineLeft;
        measure.annotations.startRepeat = true;
        return newChart;
      }

      // ── LEFT BARLINE ───────────────────────────────────────────────────────
      if (type === 'barlineLeft') {
        if (measure.annotations.barlineLeft === value) {
          delete measure.annotations.barlineLeft;
          return newChart;
        }

        // Left side exclusivity: left barline and start repeat can't stack
        delete measure.annotations.startRepeat;
        measure.annotations.barlineLeft = value;
        return newChart;
      }

      // ── RIGHT BARLINE ──────────────────────────────────────────────────────
      if (type === 'barlineRight') {
        if (measure.annotations.barlineRight === value) {
          delete measure.annotations.barlineRight;
          return newChart;
        }

        // Right side exclusivity: right barline and end repeat can't stack
        if (measure.annotations.endRepeat === true) {
          delete measure.annotations.endRepeat;
          // Keep volta consistency if the end repeat was anchoring an ending span
          for (let i = measureIndex; i >= Math.max(0, measureIndex - MAX_SCAN); i--) {
            if (newChart[i].annotations?.ending !== undefined) {
              clearVoltaBlock(newChart, i, newChart[i].annotations!.ending!);
              break;
            }
          }
        }

        measure.annotations.barlineRight = value;
        return newChart;
      }

      // ── DEFAULT TOGGLE ─────────────────────────────────────────────────────
      if (measure.annotations[type] === value) {
        delete measure.annotations[type];
      } else {
        measure.annotations[type] = value;
      }
      return newChart;
    });
  }, [updateSourceChart, hasFeature, upgradeToPremium]);

  const handleLyricChange = useCallback((measureIndex: number, newLyric: string) => {
    updateSourceChart(prevChart => {
      const newChart = [...prevChart];
      newChart[measureIndex] = { ...newChart[measureIndex], lyrics: newLyric };
      return newChart;
    });
  }, [updateSourceChart]);

  const handleCopyMeasure = useCallback((measureIndex: number) => {
    if (mode !== 'edition') return;
    const measureToCopy = sourceChart[measureIndex];
    if (measureToCopy) {
      setClipboard(measureToCopy.chords);
    }
  }, [sourceChart, mode]);

  const handlePasteMeasure = useCallback((measureIndex: number) => {
    if (clipboard && mode === 'edition') {
      updateSourceChart(prevChart => {
        const newChart = [...prevChart];
        const measureToUpdate = { ...newChart[measureIndex] };
        measureToUpdate.chords = [...clipboard];
        newChart[measureIndex] = measureToUpdate;
        return newChart;
      });
    }
  }, [clipboard, updateSourceChart, mode]);

  const handleTimeSignatureChange = useCallback((newBeats: number) => {
    setTimeSignature(prev => ({ ...prev, beats: newBeats }));
  }, []);

  const handleTranspose = useCallback((amount: number) => {
    setTransposeAmount(prev => Math.max(-12, Math.min(12, prev + amount)));
  }, []);

  const handleLyricsSizeChange = useCallback((amount: number) => {
    const currentSize = uiSettings.lyricsFontSize || 14;
    const newSize = Math.max(10, Math.min(24, currentSize + amount));
    setUiSettings(prev => ({ ...prev, lyricsFontSize: newSize }));
  }, [uiSettings.lyricsFontSize]);

  const handleGigChordScaleChange = useCallback((delta: number) => {
    const current = uiSettings.gigChordScale ?? 1.0;
    const next = Math.round(Math.max(0.7, Math.min(2.5, current + delta)) * 100) / 100;
    setUiSettings(prev => ({ ...prev, gigChordScale: next }));
  }, [uiSettings.gigChordScale]);

  // ── Return ─────────────────────────────────────────────────────

  return {
    // State
    songTitle,
    songAuthor,
    songStyle,
    numberOfMeasures,
    timeSignature,
    keySignature,
    sourceChart,
    transposeAmount,
    audioSettings,
    uiSettings,
    history,
    activeChord,
    invalidChords,
    clipboard,
    // Computed
    displayedChart,
    repeatPairMap,
    recordedNotes,
    // Setters
    setSongTitle,
    setSongAuthor,
    setSongStyle,
    setNumberOfMeasures,
    setTimeSignature,
    setKeySignature,
    setSourceChart,
    setTransposeAmount,
    setAudioSettings,
    setUiSettings,
    setActiveChord,
    setRecordedNotes,
    // Core functions
    updateSourceChart,
    applyLoadedState,
    // Handlers
    handleUndo,
    handleNumMeasuresChange,
    handleSubdivisionChange,
    handleChordClick,
    handleChordBuild,
    handleAnnotateMeasure,
    handleLyricChange,
    handleCopyMeasure,
    handlePasteMeasure,
    handleTimeSignatureChange,
    handleTranspose,
    handleLyricsSizeChange,
    handleGigChordScaleChange,
  };
}
